import { CircularProgress, IconButton, Tooltip, useTheme } from '@mui/material';
import { green, red } from '@mui/material/colors';
import { I18nTranslator } from '@strength/i18n';
import { Link } from '@tanstack/react-router';
import { MdCheck, MdClose, MdPerson, MdWarning } from '@teamworksdev/icons';
import { CSS } from '@dnd-kit/utilities';
import { DateTime } from 'luxon';
import { CSSProperties, MouseEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { useQueryOne } from '../../../../../loco-sync';
import { trackEvent } from '../../../../../reporting/reporting';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../../../components/context-menu/context-menu';
import {
  deleteBlockText,
  DeleteConfirmationModal,
} from '../../../../components/modals/delete-confirmation-modal';
import { DisabledWithTooltip } from '../../../../components/permissions/DisabledWithTooltip';
import { cn } from '../../../../components/utils';
import { useFlags } from '../../../../context/featureFlag';
import { useViewer } from '../../../../context/viewer';
import { useToast } from '../../../../hooks/useToast';
import { blockDragGate } from '../annual-planner-access';
import { Block, TimelineItem } from '../types';
import { hexWithAlpha } from './cross-team-colors';
import { MoveAthleteBlockModal } from './MoveAthleteBlockModal';

// Minimal block shape required to render a block on the timeline. The editable
// path receives a full `Block` (which extends this); the read-only path can
// pass a thinner cross-team block payload that omits loco-sync / mutation
// metadata. Picking from `Block` keeps `status` as a proper union so
// comparisons like `=== 'canceled'` stay type-checked.
export type BlockItemBlock = Pick<
  Block,
  'id' | 'name' | 'startDate' | 'endDate' | 'status'
>;

type BlockItemBaseProps = TimelineItem & {
  teamId: string;
  style: CSSProperties;
  selectedAthleteId?: string;
  effectiveColor?: string | null;
};

type BlockItemEditableProps = BlockItemBaseProps & {
  readOnly?: false;
  block: Block;
  hideHoverButton: () => void;
  setBlockIdToEdit: (id: string) => void;
  deleteBlock: (blockId: string) => void;
  cancelBlock: (blockId: string) => void;
  setBlockToCopy: (block: Block) => void;
  /**
   * True when the parent renders a DndContext wired for block drags (the
   * drag-interactions flag is on and the coach may edit blocks). The
   * draggable variant of the block mounts only then.
   */
  dragActive?: boolean;
  /** How the block renders while a drag move is being decided or saved. */
  dragState?: 'pending' | 'saving';
};

type BlockItemReadOnlyProps = BlockItemBaseProps & {
  readOnly: true;
  block: BlockItemBlock;
  // Only set for a block rendered on another team's cross-team row — adds
  // `?crossTeamId=` to the block link so the destination page knows it's
  // being opened from a cross-team context. Omit for same-team read-only
  // blocks (e.g. an archived track's blocks).
  crossTeamId?: string;
};

type BlockTimelineItemProps = BlockItemEditableProps | BlockItemReadOnlyProps;

/**
 * Builds the block link's search params. Extracted as a pure function so the
 * `crossTeamId`-omission behavior (a same-team block must never get
 * `?crossTeamId=`) can be unit tested without rendering the component.
 */
export function buildBlockLinkSearch(
  selectedAthleteId?: string,
  crossTeamId?: string,
): { athleteId?: string; crossTeamId?: string } {
  return {
    ...(selectedAthleteId ? { athleteId: selectedAthleteId } : {}),
    ...(crossTeamId ? { crossTeamId } : {}),
  };
}

/**
 * Visual primitive — a calendar-positioned wrapper that owns the left/width
 * math plus the tooltip + Link + canceled-opacity treatment. The wrapper
 * itself is the absolutely-positioned element so callers can render a
 * sibling next to the Link (e.g. the editable transfer button); the Link
 * fills the wrapper via `inset-0`. Inline children (icons) render inside
 * the Link.
 */
// Shape of the dnd-kit draggable wiring BlockLink spreads onto its
// positioning div when the block is draggable (Phase 04). `dragProps` is
// kept loose: it carries the sensor listeners and ARIA attributes verbatim.
type BlockLinkDrag = {
  setNodeRef: (node: HTMLElement | null) => void;
  dragProps: Record<string, unknown>;
  transform?: string;
  dragging?: boolean;
};

function BlockLink({
  block,
  teamId,
  dateHeaderScale,
  timelineStart,
  style,
  selectedAthleteId,
  crossTeamId,
  effectiveColor,
  tooltipTitle,
  onMouseMove,
  children,
  trailingSibling,
  drag,
  notMovable,
  pending,
}: {
  block: BlockItemBlock;
  teamId: string;
  dateHeaderScale: number;
  timelineStart: DateTime;
  style: CSSProperties;
  selectedAthleteId?: string;
  crossTeamId?: string;
  effectiveColor?: string | null;
  tooltipTitle?: string;
  onMouseMove?: (event: MouseEvent<HTMLAnchorElement>) => void;
  children?: ReactNode;
  trailingSibling?: ReactNode;
  drag?: BlockLinkDrag;
  /** Shows the visible not-movable affordance (Phase 04). */
  notMovable?: boolean;
  /**
   * A drag move being decided (pending) or saved: the block dims and stops
   * responding while the Confirm New Start Date modal owns the decision.
   */
  pending?: boolean;
}) {
  const startDatetime = DateTime.fromISO(block.startDate);
  const left = startDatetime.diff(timelineStart).as('days') * dateHeaderScale;
  const width =
    DateTime.fromISO(block.endDate).diff(startDatetime).as('days') *
    dateHeaderScale;
  const isBlockCanceled = block.status === 'canceled';

  return (
    <div
      ref={drag?.setNodeRef}
      data-not-movable={notMovable || undefined}
      className={cn(
        'absolute inset-y-0 z-20',
        drag?.dragging && 'z-30',
        notMovable && 'cursor-not-allowed',
        !notMovable && drag && 'cursor-grab',
      )}
      style={{ left, width, transform: drag?.transform, ...style }}
      {...(drag?.dragProps ?? {})}
    >
      <Tooltip
        disableInteractive
        placement="top"
        title={tooltipTitle ?? block.name}
        arrow
      >
        <Link
          data-block-id={block.id}
          className={cn(
            'absolute inset-0 flex items-center justify-between border rounded-md border-gray-300 hover:bg-gray-200 p-2 shadow-sm',
            !effectiveColor && 'bg-white',
            isBlockCanceled && 'opacity-60',
            pending && 'pointer-events-none opacity-70',
          )}
          style={
            effectiveColor
              ? {
                  backgroundColor: hexWithAlpha(effectiveColor, 0.15),
                  borderLeft: `3px solid ${effectiveColor}`,
                }
              : undefined
          }
          onMouseMove={onMouseMove}
          to="/$teamId/block/$blockId"
          params={{ teamId, blockId: block.id }}
          search={buildBlockLinkSearch(selectedAthleteId, crossTeamId)}
        >
          <span className="flex flex-row justify-between items-center w-full">
            <span className="text-sm truncate w-full">{block.name}</span>
            {children}
          </span>
        </Link>
      </Tooltip>
      {trailingSibling}
    </div>
  );
}

function ReadOnlyBlock({ crossTeamId, ...props }: BlockItemReadOnlyProps) {
  const { team } = useViewer();
  return (
    <BlockLink
      {...props}
      teamId={team.selectedTeam}
      crossTeamId={crossTeamId}
    />
  );
}

const UNPUBLISHED = 'unpublished';
const PUBLISHED = 'published';
const IN_PROGRESS = 'inprogress';
type DeleteModalType =
  | typeof UNPUBLISHED
  | typeof PUBLISHED
  | typeof IN_PROGRESS;

export const BlockItem = (props: BlockTimelineItemProps) => {
  if (props.readOnly) {
    return <ReadOnlyBlock {...props} />;
  }
  return <EditableBlock {...props} />;
};

const EditableBlock = (props: BlockItemEditableProps) => {
  const { dragInteractionsFlag } = useFlags();
  const { hasPermissions } = useViewer();
  // Same rules as the Edit Block Drawer's start-date lock, extended with the
  // Blocks Edit permission (blockDragGate). The draggable wrapper mounts only
  // when the flag is on, the parent provides a DndContext (dragActive), and
  // the block is movable for a coach who may edit — useDraggable requires a
  // DndContext, so gating decides which component renders.
  const gate = blockDragGate(
    props.block,
    hasPermissions('plan.blocks', 'edit'),
  );
  if (dragInteractionsFlag && props.dragActive && gate.movable) {
    return <DraggableBlock {...props} />;
  }
  return <EditableBlockContent {...props} />;
};

/**
 * The draggable variant of the editable block: wires the block into the
 * timeline's DndContext and applies the drag transform. The block payload
 * travels in the active node's data, so the drop handler can compute the
 * move without looking the block up again.
 */
function DraggableBlock(props: BlockItemEditableProps) {
  const { listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: props.block.id,
    data: { block: props.block },
  });
  return (
    <EditableBlockContent
      {...props}
      drag={{
        setNodeRef,
        // Pointer listeners only: dnd-kit's attributes would announce the
        // wrapper as a draggable button (role + aria-roledescription), but
        // no KeyboardSensor is registered — the pointer-free move path is
        // the Edit Block Drawer (WCAG 2.5.7), so the wrapper must not claim
        // an interactive role it cannot honor. The block link stays the
        // focusable element and the focus-restore target.
        dragProps: { ...listeners },
        transform: CSS.Translate.toString(transform) || undefined,
        dragging: isDragging,
      }}
    />
  );
}

const EditableBlockContent = ({
  block,
  teamId,
  dateHeaderScale,
  timelineStart,
  hideHoverButton,
  setBlockIdToEdit,
  deleteBlock,
  cancelBlock,
  setBlockToCopy,
  style,
  selectedAthleteId,
  effectiveColor,
  drag,
  dragState,
}: BlockItemEditableProps & {
  drag?: {
    setNodeRef: (node: HTMLElement | null) => void;
    dragProps: Record<string, unknown>;
    transform?: string;
    dragging?: boolean;
  };
}) => {
  const { t } = useTranslation();
  const isBlockCanceled = block.status === 'canceled';

  const {
    org: {
      settings: { enableTeamLibrary },
    },
    hasPermissions,
  } = useViewer();
  const showTransferIcon = !enableTeamLibrary;

  const { dragInteractionsFlag } = useFlags();
  const gate = blockDragGate(block, hasPermissions('plan.blocks', 'edit'));

  const [deleteModalOpen, setDeleteModalOpen] =
    useState<DeleteModalType | null>(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const { data: publishTask } = useQueryOne('BlockPublishTask', {
    blockId: block.id,
  });
  const { toast } = useToast();

  const {
    palette: { warning, error },
  } = useTheme();

  // The main block tooltip takes precedence so we have to manually disable it
  // when hovering on the icon
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const showBlockIcon = () => {
    if (isBlockCanceled) {
      return <MdClose aria-hidden="true" color={red[400]} size={24} />;
    }

    const hasPublishErrors =
      Object.keys(publishTask?.failedWorkouts ?? {}).length > 0 ||
      publishTask?.status === 'send_failed';
    const errorIcon = hasPublishErrors ? (
      <Tooltip
        disableInteractive
        placement="top"
        title={
          publishTask?.status === 'send_failed'
            ? // SC-5458: the publish never reached the worker; retry publishes.
              t('blockPublishNotStarted')
            : t('blockHasPublishingErrors')
        }
        arrow
        onOpen={() => {
          setTooltipOpen(true);
        }}
        onClose={() => {
          setTooltipOpen(false);
        }}
      >
        <span className="flex">
          <MdWarning aria-hidden="true" color={warning.main} size={22} />
        </span>
      </Tooltip>
    ) : null;
    const publishedIcon = block.hasPublishedWorkouts ? (
      <MdCheck color={green[400]} size={24} aria-hidden="true" />
    ) : null;

    return (
      <div className="flex space-x-2 items-center">
        {errorIcon}
        {publishedIcon}
      </div>
    );
  };

  const transferButton =
    showTransferIcon &&
    block.transferredAthletes &&
    block.transferredAthletes.length > 0 ? (
      // Moving the block to another team rewrites its teamId, so the icon
      // is gated by the same Blocks Edit key as the block's other edits.
      <DisabledWithTooltip
        disabled={!hasPermissions('plan.blocks', 'edit')}
        reason={t('permissionDisabled.edit', { item: t('blocks') })}
      >
        <IconButton
          aria-label={t('moveAthleteBlock')}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setTransferModalOpen(true);
          }}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 30,
            border: 1.5,
            borderRadius: 2,
            p: 0,
            borderColor: error.main,
          }}
        >
          <MdPerson aria-hidden="true" color={error.main} size={20} />
        </IconButton>
      </DisabledWithTooltip>
    ) : null;

  // Hover reason: while the drag flag is on, a block the coach cannot move
  // explains why on hover — a lock reason for bowling/in-progress blocks, the
  // permission reason for a revoked Blocks Edit. The reason is also rendered
  // as visually-hidden text so it is reachable without hover.
  const dragReason =
    dragInteractionsFlag && gate.reason
      ? gate.reason === 'permission'
        ? t('permissionDisabled.edit', { item: t('blocks') })
        : gate.reason === 'bowling'
          ? t('startDateLockedBowling')
          : t('startDateLockedInProgress')
      : null;

  const pending = dragState === 'pending';
  const saving = dragState === 'saving';

  const link = (
    <BlockLink
      block={block}
      teamId={teamId}
      dateHeaderScale={dateHeaderScale}
      timelineStart={timelineStart}
      style={style}
      selectedAthleteId={selectedAthleteId}
      effectiveColor={effectiveColor}
      tooltipTitle={
        // While saving, a saving state takes over from the hover tooltip; a
        // pending block keeps the tooltip quiet so the modal carries the
        // decision instead.
        saving
          ? t('blockMoveSaving')
          : dragReason
            ? dragReason
            : tooltipOpen
              ? ''
              : block.name
      }
      notMovable={!!dragReason && gate.reason !== 'permission'}
      pending={pending || saving}
      drag={drag}
      onMouseMove={(e) => {
        e.stopPropagation();
        hideHoverButton();
      }}
      trailingSibling={transferButton}
    >
      <span className="flex">
        {saving && (
          <CircularProgress
            size={16}
            aria-label={t('blockMoveSaving')}
            color="inherit"
          />
        )}
        {showBlockIcon()}
      </span>
      {(pending || saving) && (
        <span aria-busy="true" className="sr-only">
          {saving ? t('blockMoveSaving') : t('blockMovePending')}
        </span>
      )}
      {dragReason && <span className="sr-only">{dragReason}</span>}
    </BlockLink>
  );

  const getDeleteModalText = (
    type: DeleteModalType | null,
    t: I18nTranslator,
  ) => {
    switch (type) {
      case UNPUBLISHED:
        return {
          ...deleteBlockText(t),
          cancelText: t('close'),
          confirmText: t('yesDelete'),
        };
      case PUBLISHED:
        return {
          ...deleteBlockText(t),
          cancelText: t('close'),
          confirmText: t('yesCancel'),
          message: t('deletePublishedBlockConfirm', { name: block.name }),
        };
      case IN_PROGRESS:
        return {
          title: t('cancelBlock'),
          cancelText: t('close'),
          confirmText: t('yesCancel'),
          message: t('cancelInProgressBlockConfirm', { name: block.name }),
        };
      case null:
      default:
        return {
          title: '',
          message: '',
        };
    }
  };

  const reportEvent = () => {
    if (deleteModalOpen === PUBLISHED) {
      trackEvent('Strength | Annual Planner | Cancel Published Block', {
        blockId: block.id,
      });
    } else if (deleteModalOpen === IN_PROGRESS) {
      trackEvent('Strength | Annual Planner | Cancel In Progress Block', {
        blockId: block.id,
      });
    } else {
      trackEvent('Strength | Annual Planner | Delete Block', {
        blockId: block.id,
      });
    }
  };

  return (
    <>
      <ContextMenu modal={false}>
        <ContextMenuTrigger>{link}</ContextMenuTrigger>
        <ContextMenuContent>
          <DisabledWithTooltip
            disabled={!hasPermissions('plan.blocks', 'edit')}
            reason={t('permissionDisabled.edit', { item: t('blocks') })}
          >
            <ContextMenuItem onClick={() => setBlockIdToEdit(block.id)}>
              {t('editBlock')}
            </ContextMenuItem>
          </DisabledWithTooltip>
          <DisabledWithTooltip
            disabled={!hasPermissions('plan.blocks', 'create')}
            reason={t('permissionDisabled.create', { item: t('blocks') })}
          >
            <ContextMenuItem onClick={() => setBlockToCopy(block)}>
              {t('copyBlock')}
            </ContextMenuItem>
          </DisabledWithTooltip>
          {block.status !== 'canceled' && block.hasWorkoutsInProgress && (
            <DisabledWithTooltip
              disabled={!hasPermissions('plan.blocks', 'delete')}
              reason={t('permissionDisabled.delete', { item: t('blocks') })}
            >
              <ContextMenuItem
                onClick={() => {
                  setDeleteModalOpen(IN_PROGRESS);
                }}
              >
                {t('cancelBlock')}
              </ContextMenuItem>
            </DisabledWithTooltip>
          )}

          {!block.hasWorkoutsInProgress && (
            <DisabledWithTooltip
              disabled={!hasPermissions('plan.blocks', 'delete')}
              reason={t('permissionDisabled.delete', { item: t('blocks') })}
            >
              <ContextMenuItem
                onClick={() =>
                  setDeleteModalOpen(
                    block.hasPublishedWorkouts ? PUBLISHED : UNPUBLISHED,
                  )
                }
              >
                {t('deleteBlock')}
              </ContextMenuItem>
            </DisabledWithTooltip>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {deleteModalOpen && (
        <DeleteConfirmationModal
          id={block.id}
          open={!!deleteModalOpen}
          {...getDeleteModalText(deleteModalOpen, t)}
          onClose={() => setDeleteModalOpen(null)}
          onDelete={() => {
            if (deleteModalOpen === IN_PROGRESS) {
              cancelBlock(block.id);
            } else {
              deleteBlock(block.id);
            }
            reportEvent();
            setDeleteModalOpen(null);
          }}
        />
      )}

      {transferModalOpen && (
        <MoveAthleteBlockModal
          open={transferModalOpen}
          onClose={() => setTransferModalOpen(false)}
          blockId={block.id}
          teamId={teamId}
          onSave={(count: number) => {
            setTransferModalOpen(false);
            toast({
              severity: 'success',
              title: t('success'),
              description: t('athleteBlocksTransferred', { count }),
            });
          }}
        />
      )}
    </>
  );
};
